// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadMessages from "@insite/client-framework/Store/Data/Messages/Handlers/LoadMessages";
import updateMessageIsRead from "@insite/client-framework/Store/Data/Messages/Handlers/UpdateMessageIsRead";
import { getMessagesDataView } from "@insite/client-framework/Store/Data/Messages/MessagesSelector";
import { MessageModel } from "@insite/client-framework/Types/ApiModels";
import { useSession } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useMessagesDataView } from "@toolkit/Framework/Store/Data/Messages/MessagesHook";
import { useEffect, useMemo } from "react";
import { useStore } from "react-redux";

const messagesParameter = {};

export interface UseNotificationsResult {
    /** All notifications, newest first. */
    messages: MessageModel[];
    /** Number of unread notifications. */
    unreadCount: number;
    isLoading: boolean;
    hasLoaded: boolean;
    /** Flip the read status of one or many notifications (skips ones already in the target state). */
    setMessagesReadStatus: (messages: MessageModel[], isRead: boolean) => void;
}

/**
 * Shared data source for the desktop Notifications flyout and the mobile Notifications panel.
 * A "notification" is a platform Message — see CONTEXT.md "Site Notifications". Keeps the
 * NotificationsPanel/NotificationList components presentational (props only).
 */
const useNotifications = (): UseNotificationsResult => {
    const dispatch = useAppDispatch();
    const store = useStore();
    const session = useSession();
    const { value, isLoading } = useMessagesDataView(messagesParameter);

    // Messages are per-user, so the API rejects anonymous callers. The bell renders regardless of
    // auth state, so gate the load the same way AccountMessagesCount does.
    const isAuthenticated = !!session?.isAuthenticated && !session.isGuest;

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        // Guard against the *live* store rather than this render's snapshot. Every
        // AccountMessagesCount in the header (including the one inside our own trigger) loads the
        // same data view on mount, and child effects run before ours in the same commit — a
        // render-time guard would still read "not loading" here and fire a duplicate request.
        const { value: currentValue, isLoading: currentIsLoading } = getMessagesDataView(
            store.getState() as ApplicationState,
            messagesParameter,
        );

        if (!currentValue && !currentIsLoading) {
            dispatch(loadMessages(messagesParameter));
        }
        // Re-runs if the session becomes authenticated; the live-store guard keeps it to one request.
    }, [isAuthenticated]);

    // Notifications are displayed from newest to oldest.
    const messages = useMemo(
        () =>
            value
                ? [...value].sort((a, b) => new Date(b.dateToDisplay).getTime() - new Date(a.dateToDisplay).getTime())
                : [],
        [value],
    );

    const unreadCount = useMemo(() => messages.filter(message => !message.isRead).length, [messages]);

    const setMessagesReadStatus = (messagesToUpdate: MessageModel[], isRead: boolean) => {
        messagesToUpdate.forEach(message => {
            if (message.isRead !== isRead) {
                dispatch(updateMessageIsRead({ message, isRead }));
            }
        });
    };

    return {
        messages,
        unreadCount,
        isLoading,
        hasLoaded: !!value,
        setMessagesReadStatus,
    };
};

export default useNotifications;
